/* lp-funnel.js — the homepage funnel: three collection tiles whose photographs rotate.
   Ported 16 Sep 2026 from the prototype's initRot/paintRot (grep HGAL in
   website/Leatherpress_Website_v4_branch2.html). Behaviour identical: every tile crossfades through
   its frames every 3s on one shared interval; hovering a tile holds it; the caption and dots exist
   for the harness and assistive tech but are visually hidden (.rothidden), as in the prototype;
   reduced-motion visitors get the first frame and no rotation — the 3s crossfade was the one
   animation on the site that had ignored the preference, fixed 21 Aug and kept fixed here.
   Frames come from the tile blocks' data attributes, not a JS table.

   2 Oct 2026 — the later frames load as they are needed (Luke approved the speed fixes that day: "go ahead and do
   them"). Measured on the live site at phone size that morning, the fifteen frames were 3.2 MB of the 3.5 MB of
   photographs the home page fetched at load, though a visitor sees one frame per tile; loading="lazy" did not
   hold them back, because the later frames sit exactly where the first one is. Now:
     - each frame is the size its tile draws it at (srcset/sizes from the section);
     - the first frame of each tile loads with the page, as before; a tile's next frame is fetched once the page
       has finished loading and the tiles are on screen (the home page's sit just below the first screen, so a
       visitor who never scrolls to them never downloads them), one frame per tile at a time, starting as soon as
       the frame before it is showing, so it has that frame's 3 seconds to arrive;
     - a tile fades only to a frame whose photograph has arrived and been decoded. If it has not, the tile keeps
       the photograph it shows and tries again on the next 3-second beat: on a slow connection the rotation
       waits, it never fades to a blank or half-drawn frame. A frame that fails to load is skipped;
     - the beat, the 1.1s crossfade, the hover hold, the dots and reduced motion are as before.
   A browser that cannot say when a photograph is decoded (no img.decode: only very old ones, and the jsdom
   harness) keeps the old way, every frame requested at once. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('lp-funnel')) return;
  var PERIOD = 3000;
  var WAIT = typeof HTMLImageElement !== 'undefined' && 'decode' in HTMLImageElement.prototype;
  var START_CAP = 20000;   /* the later frames start by then even if the page's load event is still waiting on something else
                             (10s was tried: on a 1.6 Mbps phone it set them going before the page had loaded) */
  var NEAR = '0px';        /* the tiles must be on screen for their later frames to be fetched (a margin here would
                             reach the home page's tiles from the first screen, so they would load for every visit) */
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;'); }
  function srcAttrs(it) { return (it.sizes ? ' sizes="' + esc(it.sizes) + '"' : '') + (it.srcset ? ' srcset="' + esc(it.srcset) + '"' : '') + ' src="' + esc(it.img) + '"'; }

  class LPFunnel extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      var still = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      var tiles = Array.prototype.slice.call(this.querySelectorAll('[data-tile]'));
      var unbind = [];
      function on(t, ev, fn) { t.addEventListener(ev, fn); unbind.push(function () { t.removeEventListener(ev, fn); }); }
      var groups = [];
      /* the later frames wait for both: the page loaded (or START_CAP passed) and the tiles on screen */
      var loaded = !WAIT || document.readyState === 'complete', near = !WAIT || !('IntersectionObserver' in window);
      function canFetch() { return loaded && near; }
      /* 0 not asked, 1 loading, 2 arrived and decoded, 3 failed */
      function watch(G, j) {
        var im = G.rot.children[j], done = false;
        var fin = function (ok) {
          if (done) return; done = true;
          G.st[j] = ok && im.naturalWidth > 0 ? 2 : 3;
          if (G.st[j] === 2 && G.pend === j) { G.pend = -1; G.i = j; paint(G); ahead(G); }   /* a dot asked for it */
        };
        if (im.complete && im.naturalWidth > 0 && im.getAttribute('src')) { G.st[j] = 2; return; }
        im.addEventListener('load', function () { if (im.decode) im.decode().then(function () { fin(true); }, function () { fin(im.complete); }); else fin(true); });
        im.addEventListener('error', function () { fin(false); });
      }
      function fetchFrame(G, j) {
        if (j < 0 || G.st[j]) return;
        var im = G.rot.children[j], it = G.items[j]; if (!im) return;
        G.st[j] = 1; watch(G, j);
        im.setAttribute('fetchpriority', 'low');
        if (it.sizes) im.setAttribute('sizes', it.sizes);
        if (it.srcset) im.setAttribute('srcset', it.srcset);
        im.setAttribute('src', it.img);
      }
      function ready(G, j) { var im = G.rot.children[j]; return G.st[j] === 2 && !!im && im.complete && im.naturalWidth > 0; }
      /* the frame after the one showing, passing over any that failed; -1 when there is none */
      function upcoming(G) {
        var n = G.items.length;
        for (var k = 1; k < n; k++) { var j = (G.i + k) % n; if (G.st[j] !== 3) return j; }
        return -1;
      }
      function ahead(G) { if (WAIT && canFetch()) fetchFrame(G, upcoming(G)); }
      function kick() { groups.forEach(ahead); }
      tiles.forEach(function (tile) {
        var rot = tile.querySelector('[data-rot]'), cap = tile.querySelector('[data-cap]'), dots = tile.querySelector('[data-dots]');
        var frames = [];
        try { frames = JSON.parse(tile.getAttribute('data-frames') || '[]'); } catch (e) { frames = []; }
        frames = frames.filter(function (f) { return f && f.img; });
        if (!rot || !frames.length) return;
        var G = { items: frames, i: 0, hold: false, rot: rot, cap: cap, dots: dots, st: [], pend: -1 };
        /* the first frame now; the later ones get their address when they are fetched (WAIT), or at once as before */
        rot.innerHTML = frames.map(function (it, i) {
          var later = WAIT && i > 0;
          return '<img class="' + (i === 0 ? 'on' : '') + '"' + (later ? '' : srcAttrs(it)) + ' alt="' + esc(it.cap || '') + '"' + (i === 0 || later ? '' : ' loading="lazy"') + '>';
        }).join('');
        frames.forEach(function (it, i) { G.st[i] = (WAIT && i > 0) ? 0 : 1; });
        if (WAIT) watch(G, 0);
        if (dots) {
          dots.innerHTML = frames.map(function (it, i) { return '<button type="button"' + (i === 0 ? ' class="on"' : '') + ' data-i="' + i + '" aria-label="photo ' + (i + 1) + '"></button>'; }).join('');
          on(dots, 'click', function (e) {
            var b = e.target.closest('button[data-i]'); if (!b) return; e.preventDefault(); e.stopPropagation();
            var j = parseInt(b.getAttribute('data-i'), 10);
            /* a frame not yet arrived is fetched now and shown when it has (the dots are not gated on the page load) */
            if (!WAIT || ready(G, j)) { G.pend = -1; G.i = j; paint(G); ahead(G); } else { G.pend = j; fetchFrame(G, j); }
          });
        }
        paint(G);
        on(rot, 'mouseenter', function () { G.hold = true; });
        on(rot, 'mouseleave', function () { G.hold = false; });
        groups.push(G);
      });
      function paint(G) {
        var imgs = G.rot.children;
        for (var i = 0; i < imgs.length; i++) imgs[i].classList.toggle('on', i === G.i);
        if (G.dots) { var d = G.dots.children; for (var j = 0; j < d.length; j++) d[j].classList.toggle('on', j === G.i); }
        if (G.cap) G.cap.textContent = G.items[G.i].cap || '';
      }
      if (!still && groups.length) {
        this._timer = setInterval(function () {
          groups.forEach(function (G) {
            if (G.hold) return;
            if (!WAIT) { G.i = (G.i + 1) % G.items.length; paint(G); return; }
            var j = upcoming(G); if (j < 0) return;
            /* its photograph is in: fade to it and fetch the one after; not yet: keep this one, and make sure it is coming */
            if (ready(G, j)) { G.pend = -1; G.i = j; paint(G); }
            ahead(G);
          });
        }, PERIOD);
        if (WAIT) {
          var go = function () { if (loaded) return; loaded = true; kick(); };
          if (!loaded) {
            on(window, 'load', go);
            var capT = setTimeout(go, START_CAP); unbind.push(function () { clearTimeout(capT); });
          }
          if (!near) {
            var io = new IntersectionObserver(function (es) {
              near = es.some(function (e) { return e.isIntersecting; });
              if (near) kick();
            }, { rootMargin: NEAR });
            io.observe(this.querySelector('.ftiles') || this); unbind.push(function () { io.disconnect(); });
          }
          kick();
        }
      }
      this._unbind = function () { unbind.forEach(function (f) { f(); }); };
    }
    disconnectedCallback() {
      if (this._timer) { clearInterval(this._timer); this._timer = null; }
      if (this._unbind) { this._unbind(); this._unbind = null; }
    }
  }
  customElements.define('lp-funnel', LPFunnel);
})();
